import { IconClose } from './talkAppleIcons';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { langNameJa } from '../data/languages';
import ReportModal from './ReportModal';
import './profile-sheet.css';

export default function ProfileOverlayModal({ userId, onClose, onNavigate }) {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [reload, setReload] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const reportedRef = useRef(false), panelRef = useRef(null), closeRef = useRef(onClose), triggerRef = useRef(null);
  closeRef.current = onClose;
  useEffect(() => {
    let cancelled = false;
    setPage(null); setError(''); setActionError(''); setOptionsOpen(false);
    axios.get(`/api/users/${userId}`).then(({ data }) => {
      if (!cancelled) setPage({ user: data.user, posts: data.posts || [] });
    }).catch(e => { if (!cancelled) setError(e.response?.data?.error || 'プロフィールを読み込めませんでした。'); });
    return () => { cancelled = true; };
  }, [userId, reload]);
  useEffect(() => {
    triggerRef.current = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector('button')?.focus({ preventScroll: true });
    return () => { document.body.style.overflow = overflow; if (triggerRef.current?.isConnected) triggerRef.current.focus({ preventScroll: true }); };
  }, []);
  useEffect(() => {
    const activePanel = reportOpen ? document.querySelector('.rp-overlay') : panelRef.current;
    if (reportOpen) activePanel?.querySelector('button')?.focus();
    const onKey = e => {
      if (e.key === 'Escape' && !reportOpen) { e.preventDefault(); e.stopPropagation(); if (optionsOpen) setOptionsOpen(false); else closeRef.current(); }
      if (e.key === 'Tab') {
        const buttons = [...(activePanel?.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), textarea:not(:disabled)') || [])];
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [optionsOpen, reportOpen]);
  const u = page?.user;
  const isMe = u && String(u.id) === String(me?.id);
  const toggleFollow = async () => {
    if (!u || isMe || followBusy || u.is_blocked) return;
    setFollowBusy(true); setActionError('');
    try {
      const { data } = await axios[u.is_following ? 'delete' : 'post'](`/api/users/${u.id}/follow`);
      setPage(previous => ({ ...previous, user: { ...previous.user, ...data } }));
    } catch { setActionError('フォローを変更できませんでした。もう一度お試しください。'); }
    finally { setFollowBusy(false); }
  };
  const toggleBlock = async () => {
    if (!u || isMe || blockBusy) return;
    if (!u.is_blocked && !window.confirm(`${u.username}さんをブロックしますか？`)) return;
    setBlockBusy(true); setActionError('');
    try {
      await axios[u.is_blocked ? 'delete' : 'post'](`/api/users/${u.id}/block`);
      setPage(previous => ({ ...previous, posts: u.is_blocked ? previous.posts : [], user: { ...previous.user, is_blocked: !u.is_blocked, is_following: u.is_blocked ? previous.user.is_following : false } }));
      setOptionsOpen(false);
    } catch { setActionError('ブロック設定を変更できませんでした。もう一度お試しください。'); }
    finally { setBlockBusy(false); }
  };
  return createPortal(<div className="jt-profile-sheet-backdrop" onClick={() => { if (!reportOpen) onClose(); }}>
    <section ref={panelRef} className="jt-profile-sheet" role="dialog" aria-modal="true" aria-labelledby="profile-overlay-title" onClick={e => e.stopPropagation()}>
      <div className="jt-profile-sheet-tools">
        <button data-control="icon" aria-label="プロフィールを閉じる" onClick={onClose}><IconClose size={24} /></button>
        {u && !isMe && <button data-control="icon" aria-label="ユーザーの操作" aria-expanded={optionsOpen} onClick={() => setOptionsOpen(open => !open)}><span aria-hidden="true">⋯</span></button>}
      </div>
      {optionsOpen && <div className="jt-profile-sheet-options" role="group" aria-label="ユーザーの操作">
        <button onClick={toggleBlock} disabled={blockBusy || followBusy}>{blockBusy ? '変更中…' : u?.is_blocked ? 'ブロックを解除' : 'ブロックする'}</button>
        <button onClick={() => { setOptionsOpen(false); setReportOpen(true); }}>このユーザーを通報</button>
      </div>}
      <div className="jt-profile-sheet-body">
        {!u && <h2 id="profile-overlay-title" className="jt-profile-sheet-loading-title">プロフィール</h2>}
        {!u && !error && <p role="status">読み込み中…</p>}
        {error && <div role="alert"><p>{error}</p><button className="jt-profile-sheet-secondary" onClick={() => setReload(n => n + 1)}>再読み込み</button></div>}
        {u && <>
          <div className="jt-profile-sheet-person">
            <div className="jt-profile-sheet-avatar">{u.avatar ? <img src={u.avatar} alt="" /> : <span>{Array.from(u.username || '?')[0].toUpperCase()}</span>}</div>
            <div className="jt-profile-sheet-identity">
              <h2 id="profile-overlay-title">{u.username}</h2>
              <div className="jt-profile-sheet-languages">
                {u.native_lang && <span>母語：{langNameJa(u.native_lang)}</span>}
                {u.learning_lang && <span>{langNameJa(u.learning_lang)}を学習中</span>}
              </div>
            </div>
          </div>
          {u.bio && <p className="jt-profile-sheet-bio">{u.bio}</p>}
          {!u.is_blocked && <div className="jt-profile-sheet-stats"><span><strong>{u.follower_count ?? 0}</strong> フォロワー</span><span><strong>{u.following_count ?? 0}</strong> フォロー中</span></div>}
          {!!page.posts.length && <div className="jt-profile-sheet-post"><span>最近の投稿</span><p>{Array.from(page.posts[0].content || (page.posts[0].image_url ? '写真の投稿' : '音声の投稿')).slice(0, 120).join('')}{Array.from(page.posts[0].content || '').length > 120 ? '…' : ''}</p></div>}
        </>}
        {actionError && <p role="alert">{actionError}</p>}
      </div>
      {u && <footer className="jt-profile-sheet-actions">
        {!isMe && <button data-control="primary" className="jt-profile-sheet-follow" aria-pressed={!!u.is_following} onClick={toggleFollow} disabled={followBusy || blockBusy || !!u.is_blocked}>{followBusy ? '変更中…' : u.is_blocked ? 'ブロック中' : u.is_following ? 'フォロー中' : 'フォローする'}</button>}
        <button data-control="secondary" className="jt-profile-sheet-secondary" onClick={() => { if (onNavigate) onNavigate(userId); else navigate(`/profile/${encodeURIComponent(userId)}`); onClose(); }}>プロフィールを見る</button>
      </footer>}
    </section>
    {reportOpen && u && <ReportModal targetType="user" targetId={u.id} targetName={u.username} onClose={() => { setReportOpen(false); if (reportedRef.current) onClose(); }} onReported={() => { reportedRef.current = true; }} />}
  </div>, document.body);
}
